import type { ComponentProps } from "react";
import { cn } from "@/lib/cn";

/**
 * ORAI's own mark: the platform default on every screen, and what every tenant shows unless a
 * platform admin has let them white-label.
 *
 * THE ARTWORK LIVES IN ONE PLACE -- `MarkPaths` below. The official ORAI logo has not been added
 * to the repo yet, so these are the hexagon-and-dot the navigation rail already used, which means
 * nothing on screen changes until the real mark lands. When it does, replace `MarkPaths` (and
 * the viewBox if it differs) and every screen picks it up.
 *
 * Drawn in `currentColor`, never a fixed fill, so it inherits the text colour of wherever it is
 * placed -- that is what keeps it legible in light and dark mode and on any tenant surface.
 */

type Variant = "mark" | "full";

interface OraiLogoProps extends Omit<ComponentProps<"span">, "children"> {
  variant?: Variant;
  /** Height of the mark in pixels. The wordmark scales with it. */
  size?: number;
  /** Accessible name. Pass "" when a neighbouring element already names the brand. */
  title?: string;
}

function MarkPaths() {
  return (
    <>
      <polygon
        points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" fill="currentColor" />
    </>
  );
}

export function OraiLogo({
  variant = "mark",
  size = 20,
  title = "ORAI",
  className,
  ...props
}: OraiLogoProps) {
  const decorative = title === "";
  return (
    <span
      className={cn("inline-flex items-center gap-2 text-current", className)}
      {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": title })}
      {...props}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <MarkPaths />
      </svg>
      {variant === "full" && (
        <span
          className="font-semibold tracking-tight"
          style={{ fontSize: Math.round(size * 0.75) }}
          aria-hidden="true"
        >
          ORAI
        </span>
      )}
    </span>
  );
}
